import { supabase } from "@/lib/supabase";
import { getUserPrivateProfile } from "@/hooks/useUserPrivateProfile";
import { Community } from "@/utils/communityUtils";
import { useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Hook für User Community (inkl. member_count)
 * * Behebt das Problem, dass getUserCommunity bei jedem Render aufgerufen wird.
 * * Cache: 1 Stunde (Community ändert sich selten)
 */
export function useUserCommunity(userId: string | null) {
  const queryClient = useQueryClient();

  return useQuery<(Community & { member_count: number }) | null, Error>({
    queryKey: ["user_community", userId],
    queryFn: async () => {
      if (!userId) return null;

      // 1. Hole Community ID über die geteilte user_private-Zeile statt eines
      // eigenen Requests (siehe useUserPrivateProfile).
      const privateProfile = await getUserPrivateProfile(queryClient, userId);
      const communityId = privateProfile.community_id;
      if (!communityId) {
        return null;
      }

      // 2. Hole Community Details
      const { data: community, error: communityError } = await supabase
        .from("communities")
        .select("*")
        .eq("id", communityId)
        .single();

      if (communityError || !community) {
        return null;
      }

      return {
        ...community,
        member_count: community.member_count ?? 0,
        price_tier: community.price_tier as "start" | "aufbau" | "etabliert",
      };
    },
    enabled: !!userId,
    staleTime: 60 * 60 * 1000, // 1 Stunde Cache (Community ändert sich selten)
    gcTime: 2 * 60 * 60 * 1000, // 2 Stunden im Speicher
    // WICHTIG: Verhindert aggressives Neuladen bei UI-Interaktionen
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
